import { SaveCancelWidget } from 'components';
import { MappingActionTypes, MappingContext } from 'contexts/MappingContext';
import { CACHE_FIRST, EDITOR_PATH_ID, ENTITY_PATH_ID } from 'contracts/constants';
import type { ICollectionType, ISchema } from 'contracts/interfaces';
import type { ActionButtonProps } from 'contracts/interfaces/ActionButtonProps';
import _ from 'lodash';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  GetMappingDocument,
  useGetMappingQuery,
  useUpdateMappingSchemaMutation
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

export const MappingActionButtons: FC<ActionButtonProps> = ({ isEditable }) => {
  const { pathname } = useLocation();
  const params: any = getParams(pathname, [EDITOR_PATH_ID, ENTITY_PATH_ID]);
  const ctx = useContext(MappingContext);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setIsSaved(false);
  }, [ctx?.state.mappingSchema]);

  const { data, loading, refetch } = useGetMappingQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    ...CACHE_FIRST
  });

  /* TODO: Handle the error from this operation. Ticket for the implementation:
     https://deitycommerce.atlassian.net/browse/DI-564 */
  const [updateMappingSchema, { loading: isSaving }] = useUpdateMappingSchemaMutation({
    refetchQueries: [
      {
        query: GetMappingDocument,
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId
        }
      }
    ]
  });

  const saveMapping = () => {
    if (params?.entityId && ctx?.state) {
      updateMappingSchema({
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId,
          schema: ctx.state.mappingSchema
        },
        notifyOnNetworkStatusChange: true
      }).then(() => setIsSaved(true));
    }
  };

  const cancelChange = () => {
    if (ctx && data) {
      ctx.dispatch({
        type: MappingActionTypes.INIT_MAPPING,
        payload: {
          mapping: {
            schema: data.getMapping.schema as ICollectionType,
            uuid: params.entityId,
            packageVersionUuid: data.getMapping.packageVersionUuid,
            name: data.getMapping.name,
            description: data.getMapping.description
          },
          source: {
            schema: ctx.state.sourceSchema as ISchema,
            packageVersionUuid: ctx.state.sourceEntity?.packageVersionUuid as string,
            uuid: ctx.state.sourceEntity?.uuid as string,
            name: ctx.state.sourceEntity?.name as string,
            description: ctx.state.sourceEntity?.description
          },
          target: {
            schema: ctx.state.targetSchema as ISchema,
            packageVersionUuid: ctx.state.targetEntity?.packageVersionUuid as string,
            uuid: ctx.state.targetEntity?.uuid as string,
            name: ctx.state.targetEntity?.name as string,
            description: ctx.state.targetEntity?.description
          },
          locked: ctx.state.locked
        }
      });
    }
    // window.location.reload();
  };

  const renderCondition =
    isEditable &&
    !isSaved &&
    data?.getMapping.schema &&
    data?.getMapping.schema &&
    ctx?.state.mappingSchema &&
    !_.isEqual(data.getMapping.schema, ctx.state.mappingSchema);

  return isSaving || renderCondition ? (
    <SaveCancelWidget onCancel={cancelChange} onSave={saveMapping} isSaving={isSaving} />
  ) : null;
};
